/* global theme */

class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-cart-discount-form]');
    this.input = this.querySelector('[name="discount"]');
    this.message = this.querySelector('[data-cart-discount-message]');
    this.clearButton = this.querySelector('[data-cart-discount-clear]');

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClear = this.handleClear.bind(this);

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.handleClear);
    }
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit);
    }

    if (this.clearButton) {
      this.clearButton.removeEventListener('click', this.handleClear);
    }
  }

  handleSubmit(evt) {
    evt.preventDefault();

    const code = this.input ? this.input.value.trim() : '';
    if (!code) {
      this.showMessage(this.dataset.emptyMessage || 'Enter a discount code.', 'error');
      return;
    }

    this.updateDiscount(code, 'apply');
  }

  handleClear(evt) {
    evt.preventDefault();
    this.updateDiscount('', 'remove');
  }

  async updateDiscount(discount, action) {
    this.setLoading(true);

    try {
      const data = await this.fetchDiscountUpdate(discount);

      if (action === 'apply' && !this.discountWasApplied(data, discount)) {
        throw new Error(this.dataset.errorMessage || 'Discount code could not be applied.');
      }

      if (action === 'remove' && !this.discountWasRemoved(data)) {
        throw new Error(this.dataset.removeErrorMessage || 'Discount code could not be removed.');
      }

      this.refreshSections(data);

      const freshElement = document.querySelector('cart-discount') || this;
      const message = action === 'remove'
        ? (this.dataset.removedMessage || 'Discount removed.')
        : (this.dataset.successMessage || 'Discount applied.');

      freshElement.showMessage(message, 'success');

      if (freshElement.input && action === 'apply') {
        freshElement.input.value = '';
      }
    } catch (error) {
      this.showMessage(error.message || this.dataset.errorMessage || 'Discount code could not be applied.', 'error');
    } finally {
      this.setLoading(false);
      const freshElement = document.querySelector('cart-discount');
      if (freshElement && freshElement !== this) {
        freshElement.setLoading(false);
      }
    }
  }

  async fetchDiscountUpdate(discount) {
    const sectionsToUpdate = ['page-header', 'cart-drawer']
      .map((selector) => document.querySelector(selector))
      .filter((section) => section && section.dataset.sectionId);

    this.sectionsToUpdate = sectionsToUpdate;

    const response = await fetch(theme.routes.cartUpdate, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        discount,
        sections: sectionsToUpdate.map((section) => section.dataset.sectionId),
        sections_url: window.location.pathname
      })
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.status || data.errors) {
      throw new Error(this.getErrorMessage(data));
    }

    return data;
  }

  refreshSections(data) {
    if (!data.sections || !this.sectionsToUpdate.length) {
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
      return;
    }

    this.sectionsToUpdate.forEach((section) => {
      const sectionId = section.dataset.sectionId;
      const html = data.sections[sectionId];

      if (!html) return;

      if (typeof section.updateFromCartChange === 'function') {
        section.updateFromCartChange(html);
      } else {
        section.innerHTML = html;
      }
    });
  }

  discountWasApplied(cart, discount) {
    const normalizedDiscount = this.normalizeDiscount(discount);
    const previousTitles = this.getPreviousDiscountTitles().map((title) => this.normalizeDiscount(title));
    const currentTitles = this.getDiscountTitles(cart).map((title) => this.normalizeDiscount(title));
    const currentDiscountTotal = Number(cart.total_discount || 0);
    const previousDiscountTotal = Number(this.dataset.currentDiscountTotal || 0);

    return currentTitles.includes(normalizedDiscount)
      || previousTitles.includes(normalizedDiscount)
      || currentDiscountTotal !== previousDiscountTotal;
  }

  discountWasRemoved(cart) {
    const previousTitles = this.getPreviousDiscountTitles().map((title) => this.normalizeDiscount(title));

    if (!previousTitles.length) {
      return true;
    }

    const currentTitles = this.getDiscountTitles(cart).map((title) => this.normalizeDiscount(title));
    return previousTitles.every((title) => !currentTitles.includes(title));
  }

  getPreviousDiscountTitles() {
    try {
      const titles = JSON.parse(this.dataset.appliedDiscounts || '[]');
      return Array.isArray(titles) ? titles : [];
    } catch (error) {
      return [];
    }
  }

  getDiscountTitles(cart) {
    const titles = [];
    const addTitle = (value) => {
      if (value) titles.push(value);
    };

    (cart.cart_level_discount_applications || []).forEach((discount) => {
      addTitle(discount.title || discount.code);
    });

    (cart.discount_applications || []).forEach((discount) => {
      addTitle(discount.title || discount.code);
    });

    (cart.items || []).forEach((item) => {
      (item.discounts || []).forEach((discount) => {
        addTitle(discount.title || discount.code);
      });

      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const discountApplication = allocation.discount_application || {};
        addTitle(allocation.title || discountApplication.title || discountApplication.code);
      });
    });

    return [...new Set(titles)];
  }

  normalizeDiscount(value) {
    return String(value || '').trim().toLowerCase();
  }

  getErrorMessage(data) {
    if (data && data.description) return data.description;
    if (data && data.message) return data.message;

    return this.dataset.errorMessage || 'Discount code could not be applied.';
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);

    [this.input, this.querySelector('button[type="submit"]'), this.clearButton].forEach((element) => {
      if (element) {
        element.disabled = isLoading;
      }
    });
  }

  showMessage(text, type) {
    if (!this.message) return;

    this.message.textContent = text;
    this.message.hidden = false;
    this.message.classList.toggle('cart-discount-form__message--error', type === 'error');
    this.message.classList.toggle('cart-discount-form__message--success', type === 'success');
  }
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}
